import * as Y from 'yjs'
import type { SupabaseClient, RealtimeChannel } from '@supabase/supabase-js'
import { beginPersist, endPersist } from './sync-status'

/**
 * Núcleo de sincronización CRDT con Yjs sobre Supabase Realtime + Postgres.
 *
 * Patrón (investigado, sin servidor Y-websocket dedicado):
 *   1. Carga el estado canónico del doc desde la tabla yjs_documents (bytea).
 *   2. Lo aplica al Y.Doc local con Y.applyUpdate.
 *   3. Se suscribe a un canal Realtime privado para recibir updates en vivo.
 *   4. Propaga los updates locales por broadcast y persiste throttled.
 *
 * Yjs resuelve la fusión concurrente automáticamente (CRDT); no hay
 * merge manual ni last-write-wins dentro del doc.
 *
 * Granularidad: un Y.Doc por entidad (un doc por libro + un doc índice
 * por usuario), cada uno con su propio room y fila en yjs_documents.
 */

export interface SyncedDoc {
  doc: Y.Doc
  /** Promesa que se resuelve cuando el estado inicial ha cargado. */
  ready: Promise<void>
  destroy: () => void
  /**
   * Fuerza la persistencia final del estado en la nube usando
   * `navigator.sendBeacon`. Pensado para `beforeunload`: a diferencia
   * de un fetch normal, sendBeacon sobrevive al cierre de la pestaña,
   * evitando perder los últimos cambios locales.
   */
  flushBeacon: () => void
}

const YJS_TABLE = 'yjs_documents'
const PERSIST_DEBOUNCE_MS = 2000

/**
 * Crea un Y.Doc sincronizado para `room`.
 *
 * @param room        Nombre único del documento (ej. `book:<userId>:<bookId>`
 *                    o `book:shared:<shareId>` para rooms compartidos).
 * @param client      Cliente Supabase autenticado.
 * @param userId      UUID del usuario (para RLS en la fila).
 * @param restBase    URL base de PostgREST (`${supabaseUrl}/rest/v1`) para
 *                    el flush por sendBeacon (beforeunload). Opcional.
 * @param anonKey     API key anónima para la cabecera `apikey` del beacon.
 * @param shareId     Si se proporciona, marca la fila como un room compartido
 *                    (lectura social, Área F). En rooms compartidos la
 *                    persistencia NO reescribe `user_id` ni `share_id` tras
 *                    el INSERT inicial (solo `state`/`updated_at`), para no
 *                    violar RLS cuando un no-owner actualiza la fila.
 */
export function createSyncedDoc(
  room: string,
  client: SupabaseClient,
  userId: string,
  restBase?: string,
  anonKey?: string,
  shareId?: string,
): SyncedDoc {
  const doc = new Y.Doc()
  let channel: RealtimeChannel | null = null
  let persistTimer: ReturnType<typeof setTimeout> | null = null
  let destroyed = false
  /** Evita loguear el error de canal repetidas veces (inunda la consola). */
  let channelErrorLogged = false

  // `origin` para distinguir updates locales de los remotos y evitar eco.
  const remoteOrigin = Symbol(`remote:${room}`)

  // ---------- 1+2. Cargar estado canónico ----------
  async function loadInitialState() {
    const { data, error } = await client
      .from(YJS_TABLE)
      .select('state')
      .eq('room', room)
      .maybeSingle()

    if (error) {
      console.warn(`[sync] no se pudo cargar ${room}:`, error.message)
      return
    }
    if (data?.state) {
      const bytes = toUint8Array(data.state)
      Y.applyUpdate(doc, bytes, remoteOrigin)
    }
  }

  // ---------- 3. Canal Realtime privado ----------
  async function connectChannel() {
    const { data } = await client.auth.getSession()
    if (data.session?.access_token) {
      client.realtime.setAuth(data.session.access_token)
    }

    channel = client.channel(room, { config: { private: true } })

    channel.on('broadcast', { event: 'update' }, ({ payload }: { payload: unknown }) => {
      try {
        const bytes = toUint8Array(payload)
        Y.applyUpdate(doc, bytes, remoteOrigin)
      } catch (e) {
        console.warn('[sync] broadcast applyUpdate falló:', e)
      }
    })

    channel.subscribe(async (status: string) => {
      if (status === 'SUBSCRIBED') {
        await loadInitialState()
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        // No loguear cada reintento (inunda la consola). El fallo de
        // Realtime no bloquea la app (offline-first). El usuario verá el
        // estado en el panel; los datos locales siguen disponibles.
        // Solo loguear la primera vez por sesión de canal.
        if (!channelErrorLogged) {
          console.warn(`[sync] canal ${room} no disponible (${status}). Modo local.`)
          channelErrorLogged = true
        }
      }
    })
  }

  // ---------- 4. Propagar updates locales ----------
  doc.on('update', (update: Uint8Array, origin: unknown) => {
    if (origin === remoteOrigin) return // llegó por broadcast, no reenviar
    if (destroyed) return

    // Broadcast a los demás clientes.
    channel
      ?.send({ type: 'broadcast', event: 'update', payload: Array.from(update) })
      .catch((e) => console.warn('[sync] broadcast falló:', e))

    // Persistencia throttled.
    if (persistTimer) clearTimeout(persistTimer)
    persistTimer = setTimeout(persistState, PERSIST_DEBOUNCE_MS)
  })

  /** Codifica el estado actual del doc como string bytea de PostgREST ("\\x<hex>"). */
  function encodeStateHex(): string {
    const state = Y.encodeStateAsUpdate(doc)
    // PostgREST espera bytea como string "\\x<hex>". Si enviamos el
    // Uint8Array directo, JSON.stringify lo convierte en un objeto
    // {0:b,1:b,...} que no se interpreta como binario → al leer vuelve
    // como basura y applyUpdate falla. Por eso codificamos a hex.
    const hex = Array.from(state, (b) => b.toString(16).padStart(2, '0')).join('')
    return `\\x${hex}`
  }

  /**
   * Persiste el estado completo del doc en yjs_documents.
   *
   * Para rooms privados: upsert simple (reescribe user_id, que es el owner).
   * Para rooms compartidos (shareId): primero intenta UPDATE (solo state +
   * updated_at, sin tocar user_id/share_id, para respetar RLS de no-owners);
   * si la fila no existe (primer miembro), INSERT con user_id del creador
   * y share_id.
   */
  async function persistState() {
    if (destroyed) return
    beginPersist()
    try {
      if (shareId) {
        // UPDATE selectivo (no reescribe user_id/share_id).
        const { error: updErr } = await client
          .from(YJS_TABLE)
          .update({ state: encodeStateHex(), updated_at: new Date().toISOString() })
          .eq('room', room)
        if (updErr) console.warn(`[sync] update falló para ${room}:`, updErr.message)
        // Comprobar si afectó filas; si no, INSERT inicial.
        // PostgREST no devuelve count por defecto; hacemos un SELECT ligero.
        const { data, error: selErr } = await client
          .from(YJS_TABLE)
          .select('room')
          .eq('room', room)
          .maybeSingle()
        if (selErr) {
          console.warn(`[sync] verificación falló para ${room}:`, selErr.message)
        } else if (!data) {
          // No existe aún: INSERT inicial con owner y share_id.
          const { error: insErr } = await client.from(YJS_TABLE).insert({
            room,
            state: encodeStateHex(),
            user_id: userId,
            share_id: shareId,
            updated_at: new Date().toISOString(),
          })
          if (insErr) console.warn(`[sync] insert compartido falló para ${room}:`, insErr.message)
        }
      } else {
        const { error } = await client.from(YJS_TABLE).upsert(
          {
            room,
            state: encodeStateHex(),
            user_id: userId,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'room' },
        )
        if (error) console.warn(`[sync] persist falló para ${room}:`, error.message)
      }
    } finally {
      endPersist()
    }
  }

  /**
   * Flush final por sendBeacon: envía el estado actual al endpoint REST
   * de PostgREST de forma que sobreviva al cierre de la pestaña.
   * No es awaited (no se puede en beforeunload); es best-effort.
   */
  async function flushBeacon() {
    if (destroyed || !restBase || !anonKey) return
    try {
      const { data } = await client.auth.getSession()
      const accessToken = data.session?.access_token
      if (!accessToken) return

      const updatedAt = new Date().toISOString()
      // En rooms compartidos usamos PATCH selectivo (por room) para no
      // reescribir user_id/share_id y respetar RLS de no-owners. En rooms
      // privados, POST upsert completo como antes.
      const method = shareId ? 'PATCH' : 'POST'
      const url = shareId
        ? `${restBase}/${YJS_TABLE}?room=eq.${encodeURIComponent(room)}`
        : `${restBase}/${YJS_TABLE}`
      const body = JSON.stringify(
        shareId
          ? { state: encodeStateHex(), updated_at: updatedAt }
          : { room, state: encodeStateHex(), user_id: userId, updated_at: updatedAt },
      )
      const blob = new Blob([body], { type: 'application/json' })
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        apikey: anonKey,
        Authorization: `Bearer ${accessToken}`,
      }
      if (!shareId) headers['Prefer'] = 'resolution=merge-duplicates,return=minimal'
      await fetch(url, {
        method,
        keepalive: true,
        headers,
        body: blob,
      }).catch((e) => console.warn(`[sync] beacon falló para ${room}:`, e))
    } catch (e) {
      console.warn(`[sync] beacon no enviado para ${room}:`, e)
    }
  }

  // ---------- Arranque ----------
  // ready se resuelve tras la carga inicial del estado remoto.
  let resolveReady!: () => void
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve
  })

  loadInitialState()
    .then(() => {
      resolveReady()
      return connectChannel()
    })
    .catch((e) => {
      console.warn(`[sync] arranque falló para ${room}:`, e)
      resolveReady() // resolver igual para no bloquear al caller
    })

  return {
    doc,
    ready,
    flushBeacon,
    destroy() {
      destroyed = true
      if (persistTimer) clearTimeout(persistTimer)
      // Última persistencia síncrona antes de cerrar.
      persistState()
      channel?.unsubscribe()
      doc.destroy()
    },
  }
}

// ---------- helpers ----------

/**
 * Convierte el payload a Uint8Array para applyUpdate.
 *
 * Cubre todos los formatos observados en la práctica:
 *   - Uint8Array / ArrayBuffer (supabase-js moderno)
 *   - number[] (broadcast por Realtime)
 *   - "\\x<hex>" (PostgREST al leer bytea — confirmado contra Supabase real)
 *   - string base64 (algunos middleware)
 */
export function toUint8Array(payload: unknown): Uint8Array {
  if (payload instanceof Uint8Array) return payload
  if (payload instanceof ArrayBuffer) return new Uint8Array(payload)
  if (Array.isArray(payload)) return new Uint8Array(payload)
  if (typeof payload === 'string') {
    // Formato bytea de PostgREST: "\\x" + hex.
    if (payload.startsWith('\\x')) {
      const hex = payload.slice(2)
      const bytes = new Uint8Array(hex.length / 2)
      for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(hex.substr(i * 2, 2), 16)
      }
      return bytes
    }
    // Fallback: base64.
    const bin = atob(payload)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    return bytes
  }
  if (payload && typeof payload === 'object' && 'buffer' in payload) {
    return new Uint8Array((payload as { buffer: ArrayBuffer }).buffer)
  }
  throw new Error('Formato de payload Yjs no reconocido')
}
